import { createAction } from '@reduxjs/toolkit';

import { isolatedDialog, isolatedHeader, type IsolatedKey } from 'Addons/fitmentSearch/isolatedKeys';
import type { VehicleActionMeta } from 'Core/actions/response';
import type { Vehicle, VehicleCollection } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import { VehicleExtra } from 'Models/vehicle';

import { clearVehicleDialogRequest } from './request';

export type VehicleSelectedMeta = Pick<
  VehicleActionMeta,
  'doNotRedirectOnVehicleSelect' | 'redirectUrl' | 'sendIsolatedRequest' | 'vehicleWidgetName'
> & {
  isSpecifyDialogOpened?: boolean;
  isVehicleSelectionForced?: boolean;
  isolatedKey?: IsolatedKey;
  isVehicleAutoSelected?: boolean;
  onInit?: boolean;
  isSelectedFromGarage?: boolean;
  isSelectedFromGarageDropdowns?: boolean;
  isSelectedFromVerifyFitment?: boolean;
};

export const vehicleDiscardingRequested = createAction(
  'VEHICLE_DISCARDING_REQUESTED',
  ({
    isInDialog,
    globalVehicleDiscardEnabled,
    mayDiscardValue,
  }: { isInDialog?: boolean; globalVehicleDiscardEnabled?: boolean; mayDiscardValue?: false } = {}) => ({
    payload: undefined,
    meta: {
      extra: { vehicleJustDiscarded: true },
      sendRequest: requestConfig.hasSearchResults,
      allowSearchSameAsUri: true,
      isInDialog,
      stayOnPage: true,
      mayDiscardValue: mayDiscardValue ?? true,
      globalVehicleDiscardEnabled,
    },
  }),
);

export const discardVehicleFieldsRequested = createAction(
  'DISCARD_VEHICLE_FIELDS_REQUESTED',
  (isolatedKey: IsolatedKey = null, fitmentFields: string[], fieldsToDiscard: string[]) => ({
    payload: undefined,
    meta: {
      allowSearchSameAsUri: true,
      extra: { vehicleJustSelected: true },
      isolatedKey,
      sendRequest: requestConfig.hasSearchResults,
      stayOnPage: true,
      fitmentFields,
      fieldsToDiscard,
    },
  }),
);

export function isolatedDiscardVehicle(
  isolatedKey: IsolatedKey,
  globalVehicleDiscardEnabled?: boolean,
): ReturnType<typeof vehicleDiscardingRequested> | ReturnType<typeof clearVehicleDialogRequest> {
  return globalVehicleDiscardEnabled
    ? vehicleDiscardingRequested({ isInDialog: isolatedKey === isolatedDialog, globalVehicleDiscardEnabled })
    : isolatedKey === isolatedDialog
      ? clearVehicleDialogRequest()
      : vehicleDiscardingRequested();
}

export const ignoreInexactFields = createAction('IGNORE_INEXACT_FIELDS');

function buildVehicleSelectedMeta(meta: VehicleSelectedMeta) {
  const explicitRedirect = meta.redirectUrl && meta.redirectUrl !== window.location.pathname;
  const sendRequest = !explicitRedirect && requestConfig.hasSearchResults;

  return {
    allowSearchSameAsUri: true,
    stayOnPage: true,
    mayDiscardValue: true,
    extra: { vehicleJustSelected: true },
    sendRequest,
    sendIsolatedRequest: meta.sendIsolatedRequest,
    doNotRedirectOnVehicleSelect: meta.doNotRedirectOnVehicleSelect,
    isSelectedFromDialog: meta.isolatedKey === isolatedDialog,
    isSelectedFromGarage: meta.isSelectedFromGarage,
    isSelectedFromGarageDropdowns: meta.isSelectedFromGarageDropdowns,
    isSelectedFromHeader: meta.isolatedKey === isolatedHeader,
    isSelectedFromSearch: meta.isolatedKey === null,
    isSelectedFromVerifyFitment: meta.isSelectedFromVerifyFitment,
    isSpecifyDialogOpened: meta.isSpecifyDialogOpened,
    isVehicleAutoSelected: meta.isVehicleAutoSelected,
    isVehicleSelectionForced: meta.isVehicleSelectionForced,
    onInit: meta.onInit,
    redirectUrl: meta.redirectUrl,
    vehicleWidgetName: meta.vehicleWidgetName,
  };
}

export const vehicleSelected = createAction(
  'VEHICLE_SELECTED',
  (vehicle: Vehicle, meta: VehicleSelectedMeta = { isolatedKey: null }) => ({
    payload: vehicle,
    meta: buildVehicleSelectedMeta(meta),
  }),
);

export const forceVehicleSelection = createAction('FORCE_VEHICLE_SELECTION');
export const forceVehicleSelectionLater = createAction('FORCE_VEHICLE_SELECTION_LATER');

export const vehicleSpecificationNeeded = createAction(
  'VEHICLE_SPECIFICATION_NEEDED',
  (fieldsToSpecify: string[], isolatedKey: IsolatedKey = null) => ({
    payload: fieldsToSpecify,
    meta: { isolatedKey },
  }),
);

export const setGarageRequested = createAction(
  'SET_GARAGE_REQUESTED',
  (payload: { vehicles: VehicleCollection; selectVehicle: boolean }) => ({
    payload,
    meta: {
      allowSearchSameAsUri: true,
      mayDiscardValue: true,
      stayOnPage: true,
    },
  }),
);

export const removalFromGarageRequested = createAction(
  'REMOVAL_FROM_GARAGE_REQUESTED',
  (vehicle: Vehicle, wasSelected: boolean) => ({
    payload: vehicle,
    meta: {
      extra: wasSelected ? { vehicleJustDiscarded: true } : {},
      wasSelected,
      sendRequest: wasSelected && requestConfig.hasSearchResults,
      allowSearchSameAsUri: true,
      stayOnPage: true,
      mayDiscardValue: true,
    },
  }),
);

export const addToGarageRequested = createAction('ADD_TO_GARAGE_REQUESTED', (vehicle: Vehicle) => ({
  payload: vehicle,
  meta: {
    stayOnPage: true,
  },
}));

export const garageCleanupRequested = createAction(
  'GARAGE_CLEANUP_REQUESTED',
  (isVehicleSelected: boolean) => ({
    payload: undefined,
    meta: {
      extra: isVehicleSelected ? { vehicleJustDiscarded: true } : {},
      sendRequest: requestConfig.hasSearchResults,
      allowSearchSameAsUri: true,
      stayOnPage: true,
      mayDiscardValue: true,
    },
  }),
);

export const vehicleColorUpdated = createAction(
  'VEHICLE_COLOR_UPDATED',
  (payload: { vehicle: Vehicle; vehicleExtra: VehicleExtra }) => ({
    payload,
    meta: {
      stayOnPage: true,
    },
  }),
);
